import { type JSX, useCallback, useEffect, useState } from 'react';

import {
  TARIFNAME,
  VEREINBARUNGSSTATUSNAME,
  type Vereinbarungszeile,
  type Vereinbarungsstatus,
  type Honorartabellenzeile,
  type Trainerhonorar,
  satzSchliessen,
  staffelEntfernen,
  staffelSetzen,
  tabelleAnlegen,
  tabelleKopieren,
  tabellen as ladeTabellen,
  trainerhonorar as ladeHonorar,
  vereinbarungAendern,
  vereinbarungAnlegen,
  vereinbarungen as ladeVereinbarungen,
} from '../api/honorare';
import { alsEurosatz, alsProzent } from '../betrag';
import { ApiFehler } from '../api/fehler';
import { alsTag } from '../datum';
import { Meldung } from '../komponenten/Meldung';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { Abschnitt, Feldraster } from '../komponenten/Akte';
import { Honorargitter } from './Honorargitter';

/**
 * Das Vertragliche und das Honorar EINES externen Trainers (Kap. 12).
 *
 * ============================================================================
 * WARUM EIGENER BAUSTEIN UND KEIN WEITERER ABSCHNITT DER KONFIGURATION
 * ============================================================================
 *
 * Die Ressourcenkonfiguration ist die Maske für jede Ressource — Raum, Modul,
 * Fahrzeug, Trainer. Rahmenvereinbarung, Absagefristen und Honorarsätze gelten
 * für genau eine Sorte davon: den externen Trainer. Ein Abschnitt mehr in einer
 * Datei von tausend Zeilen hätte ihn versteckt, und die Bedingung „nur wenn
 * extern" wäre an fünf Stellen zu wiederholen.
 *
 * ============================================================================
 * DREI SACHEN, DIE ZUSAMMENGEHÖREN UND DOCH GETRENNT SIND
 * ============================================================================
 *
 *   Die RAHMENVEREINBARUNG (RES-65) ist ein Stammdatum: Laufzeit,
 *   Kündigungsfrist, Bedingungen. Wer den Stamm pflegt, pflegt sie mit.
 *
 *   Die ABSAGESTAFFEL (RES-86) hängt daran. Ihre FRISTEN braucht das
 *   Tagesgeschäft (RES-87, AUS-24) — ihre BETRÄGE sind Honorarangaben und
 *   bleiben ohne `artikel/honorar_sehen` leer (SYS-03, RES-73).
 *
 *   Der eigene HONORARSATZ (RES-69) ist ganz und gar eine Honorarangabe. Ohne
 *   das Recht steht dieser Abschnitt nicht da.
 *
 * Die Trennung ist nicht Ordnungsliebe: Operations pflegt Ressourcen und darf
 * keine Honorare sehen (Tabelle 11). Ohne sie wäre die eine Maske für beide
 * Rollen entweder zu offen oder für die eine unbenutzbar.
 *
 * In der Aktenansicht der Ressource (ÄA-41) ist er ein aufklappbarer
 * Abschnitt wie die übrigen; Staffel und Honorar sind Unterteile darin, die
 * Eingaben stehen im Feldraster.
 */

function heute(): string {
  const jetzt = new Date();
  return [
    String(jetzt.getFullYear()),
    String(jetzt.getMonth() + 1).padStart(2, '0'),
    String(jetzt.getDate()).padStart(2, '0'),
  ].join('-');
}

function tagDavor(datum: string): string {
  const gelesen = new Date(`${datum}T12:00:00Z`);
  gelesen.setUTCDate(gelesen.getUTCDate() - 1);
  return gelesen.toISOString().slice(0, 10);
}

type Vertragsentwurf = {
  readonly kennung: string;
  readonly gueltigAb: string;
  readonly gueltigBis: string;
  readonly kuendigungsfristTage: string;
  readonly bedingungen: string;
  readonly status: Vereinbarungsstatus;
  readonly unterzeichnetAm: string;
};

type Stufenentwurf = {
  readonly vereinbarung: string;
  readonly abTagenVorEinsatz: string;
  readonly berechnung: 'prozent' | 'pauschale';
  readonly wert: string;
};

/**
 * Welche Rückfrage offen ist (CRM-104).
 *
 * Unterschreiben macht die Vereinbarung verbindlich, Kündigen beendet sie —
 * beides lässt sich in dieser Maske nicht zurückstellen (RES-65). Eine Stufe
 * geht hart aus der Staffel (RES-86).
 */
type Nachfrage =
  | { readonly art: 'unterschreiben'; readonly vertrag: Vereinbarungszeile }
  | { readonly art: 'kuendigen'; readonly vertrag: Vereinbarungszeile }
  | {
      readonly art: 'stufe';
      readonly vertrag: Vereinbarungszeile;
      readonly stufe: Vereinbarungszeile['staffel'][number];
    };

export function Trainervertrag({
  ressource,
  darfHonorar,
  darfHonorarAendern,
}: {
  readonly ressource: string;
  /** RES-73: ohne dieses Recht kein Honorarabschnitt und keine Ausfallbeträge. */
  readonly darfHonorar: boolean;
  readonly darfHonorarAendern: boolean;
}): JSX.Element {
  const [vertraege, setzeVertraege] = useState<readonly Vereinbarungszeile[]>([]);
  const [honorar, setzeHonorar] = useState<Trainerhonorar | null>(null);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [meldung, setzeMeldung] = useState<string | null>(null);
  const [laedt, setzeLaedt] = useState(true);
  const [neuerVertrag, setzeNeuerVertrag] = useState<Vertragsentwurf | null>(null);
  const [neueStufe, setzeNeueStufe] = useState<Stufenentwurf | null>(null);
  const [fassungen, setzeFassungen] = useState(false);
  /**
   * Der Tag, für den das Gitter die Sätze zeigt (0190).
   *
   * ANDERS ALS IN DER ALLGEMEINEN TABELLE geht er an den SERVER: Hier kreuzen
   * sich zwei Stufen — eigener Satz und allgemeine Vorgabe (RES-69) —, und welche
   * am Stichtag gewinnt, entscheidet `fach.honorarsatz_ermitteln`. Eine zweite
   * Fassung dieser Auswahl in der Maske wäre die, die beim nächsten Vorrang
   * auseinanderläuft.
   */
  const [stichtag, setzeStichtag] = useState(heute());
  /** Der Beginn einer neu anzulegenden eigenen Vereinbarung (0191). */
  const [eigeneAb, setzeEigeneAb] = useState(heute());
  /** Die Haussätze dabei übernehmen (0192) — vorbelegt aus. */
  const [uebernehmen, setzeUebernehmen] = useState(false);
  /** Die allgemeine Tabelle des Hauses — Vorlage für die Übernahme (0192). */
  const [allgemeine, setzeAllgemeine] = useState<Honorartabellenzeile | undefined>(undefined);

  const holen = useCallback(async () => {
    setzeLaedt(true);
    try {
      setzeVertraege(await ladeVereinbarungen(ressource));
      /*
       * Das Honorar nur holen, wenn es jemand sehen darf. Ein Aufruf ohne das
       * Recht käme als 403 zurück und erzeugte eine Meldung über etwas, das der
       * Anwender gar nicht vermisst (RES-73).
       */
      setzeHonorar(darfHonorar ? await ladeHonorar(ressource, stichtag) : null);
      /*
       * DIE ALLGEMEINE TABELLE als Vorlage für eine Übernahme (0192). Nur mit
       * dem Honorarrecht — ohne es gibt es diesen Abschnitt gar nicht (RES-73).
       */
      setzeAllgemeine(
        darfHonorar
          ? (await ladeTabellen()).find((t) => t.ressource === null && t.gilt)
          : undefined,
      );
      setzeFehler(null);
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    } finally {
      setzeLaedt(false);
    }
  }, [ressource, darfHonorar, stichtag]);

  useEffect(() => {
    void holen();
  }, [holen]);

  const handgriff = useCallback(
    async (tun: () => Promise<unknown>, sagen: string) => {
      setzeLaedt(true);
      setzeFehler(null);
      setzeMeldung(null);
      try {
        await tun();
        setzeMeldung(sagen);
        await holen();
      } catch (f) {
        setzeFehler(f instanceof ApiFehler ? f : null);
        if (!(f instanceof ApiFehler)) throw f;
      } finally {
        setzeLaedt(false);
      }
    },
    [holen],
  );

  const [nachfrage, setzeNachfrage] = useState<Nachfrage | null>(null);

  /**
   * Der Handgriff aus der Rückfrage (CRM-104): Er fängt NICHT. Der Fehler
   * steht im Pop-up, das offen bleibt — und nicht ein zweites Mal oben.
   */
  const bestaetigt = useCallback(
    async (tun: () => Promise<unknown>, sagen: string) => {
      setzeLaedt(true);
      setzeMeldung(null);
      try {
        await tun();
        setzeNachfrage(null);
        setzeMeldung(sagen);
        await holen();
      } finally {
        setzeLaedt(false);
      }
    },
    [holen],
  );

  /** Derselbe Handgriff ohne Rückgabewert — so erwartet ihn das Gitter. */
  const anpacken = useCallback(
    (tun: () => Promise<unknown>, sagen: string) => {
      void handgriff(tun, sagen);
    },
    [handgriff],
  );

  const gueltig = vertraege.find((v) => v.gilt) ?? null;

  /**
   * SEINE eigene Honorartabelle, soweit eine zum Stichtag gilt (0191).
   *
   * Es kann höchstens eine geben — `honorartabelle_hoechstens_eine_gueltige`
   * hält das. Gesucht wird mit dem STICHTAG und nicht mit heute: Wer die Sätze
   * für Januar ansieht, will auch wissen, ob im Januar überhaupt eine
   * Vereinbarung gilt.
   */
  const eigene = honorar?.tabellen.find(
    (t) =>
      t.geloeschtAm === null
      && t.gueltigAb <= stichtag
      && (t.gueltigBis === null || t.gueltigBis >= stichtag),
  );

  return (
    <Abschnitt titel="Rahmenvereinbarung und Ausfallvergütung">

      {fehler !== null && <Meldung fehler={fehler} />}
      {meldung !== null && (
        <p className="hinweiszeile" role="status">
          {meldung}
        </p>
      )}

      {/* ==================================================================
          RES-64: OHNE GÜLTIGE VEREINBARUNG KEINE BEAUFTRAGUNG
          ==================================================================

          Der Hinweis steht hier und nicht als Sperre an der Anfrage: Eine
          Anfrage ist keine Beauftragung, und die Beauftragung nach RES-60 ist
          nicht gebaut. Sichtbar muss der Zustand trotzdem sein — sonst ist
          RES-64 eine Regel, die niemand liest. */}
      {gueltig === null && (
        <div className="hinweiskarte hinweiskarte--warnung">
          <p>
            Für diesen Trainer gilt derzeit keine Rahmenvereinbarung. Anfragen bleiben
            möglich; eine Beauftragung setzt sie voraus.
          </p>
        </div>
      )}

      {vertraege.length === 0 ? (
        <p className="liste__leer">Keine Vereinbarung hinterlegt.</p>
      ) : (
        <div className="tabellenhuelle tabellenhuelle--telefon">
          <table className="liste__tabelle" aria-label="Rahmenvereinbarungen">
            <thead>
              <tr>
                <th scope="col">Kennung</th>
                <th scope="col">Laufzeit</th>
                <th scope="col">Kündigungsfrist</th>
                <th scope="col">Stand</th>
                <th scope="col">Handlung</th>
              </tr>
            </thead>
            <tbody>
              {vertraege.map((v) => (
                <tr key={v.id}>
                  <th scope="row" className="liste__nummer">{v.kennung}</th>
                  <td>
                    <span className="liste__einzeilig">{alsTag(v.gueltigAb)}</span> bis{' '}
                    <span className="liste__einzeilig">
                      {alsTag(v.gueltigBis) ?? <span className="hilfe">offen</span>}
                    </span>
                  </td>
                  <td>
                    {v.kuendigungsfristTage === null ? (
                      <span className="hilfe">nicht hinterlegt</span>
                    ) : (
                      `${String(v.kuendigungsfristTage)} Tage`
                    )}
                  </td>
                  <td>
                    <span className={v.gilt ? 'marke marke--gut' : 'marke marke--inaktiv'}>
                      {VEREINBARUNGSSTATUSNAME[v.status]}
                    </span>
                    {v.gilt && <> gilt heute</>}
                  </td>
                  <td>
                    {v.status === 'entwurf' && (
                      <button
                        type="button"
                        className="knopf knopf--klein"
                        disabled={laedt}
                        onClick={() => {
                          setzeNachfrage({ art: 'unterschreiben', vertrag: v });
                        }}
                      >
                        Als unterschrieben setzen …
                      </button>
                    )}
                    {v.status === 'aktiv' && (
                      <button
                        type="button"
                        className="knopf knopf--eingriff knopf--klein"
                        disabled={laedt}
                        onClick={() => {
                          setzeNachfrage({ art: 'kuendigen', vertrag: v });
                        }}
                      >
                        Kündigen …
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {neuerVertrag === null ? (
        <button
          type="button"
          className="knopf knopf--klein"
          onClick={() => {
            setzeNeuerVertrag({
              kennung: '',
              gueltigAb: heute(),
              gueltigBis: '',
              kuendigungsfristTage: '',
              bedingungen: '',
              status: 'entwurf',
              unterzeichnetAm: '',
            });
          }}
        >
          Vereinbarung anlegen
        </button>
      ) : (
        <>
          <Feldraster>
            <div className="feld">
              <label htmlFor="rv-kennung">Kennung</label>
              <input
                id="rv-kennung"
                value={neuerVertrag.kennung}
                onChange={(e) => {
                  setzeNeuerVertrag({ ...neuerVertrag, kennung: e.target.value });
                }}
              />
              <span className="feld__hilfe">Das Aktenzeichen des Hauses, etwa „RV-2026-07".</span>
            </div>
            <div className="feld">
              <label htmlFor="rv-ab">gültig ab</label>
              <input
                id="rv-ab"
                type="date"
                value={neuerVertrag.gueltigAb}
                onChange={(e) => {
                  setzeNeuerVertrag({ ...neuerVertrag, gueltigAb: e.target.value });
                }}
              />
            </div>
            <div className="feld">
              <label htmlFor="rv-bis">gültig bis</label>
              <input
                id="rv-bis"
                type="date"
                value={neuerVertrag.gueltigBis}
                onChange={(e) => {
                  setzeNeuerVertrag({ ...neuerVertrag, gueltigBis: e.target.value });
                }}
              />
              <span className="feld__hilfe">Leer heißt unbefristet — der Normalfall.</span>
            </div>
            <div className="feld">
              <label htmlFor="rv-frist">Kündigungsfrist in Tagen</label>
              <input
                id="rv-frist"
                className="feld__eingabe feld__eingabe--schmal"
                inputMode="numeric"
                value={neuerVertrag.kuendigungsfristTage}
                onChange={(e) => {
                  setzeNeuerVertrag({ ...neuerVertrag, kuendigungsfristTage: e.target.value });
                }}
              />
              <span className="feld__hilfe">
                Was sich rechnen lässt. Besonderheiten wie „zum Quartalsende" gehören in die
                Bedingungen.
              </span>
            </div>
          </Feldraster>
          <div className="feld">
            <label htmlFor="rv-bedingungen">Bedingungen</label>
            <textarea
              id="rv-bedingungen"
              rows={4}
              value={neuerVertrag.bedingungen}
              onChange={(e) => {
                setzeNeuerVertrag({ ...neuerVertrag, bedingungen: e.target.value });
              }}
            />
            <span className="feld__hilfe">
              Worauf der Einzelauftrag verweist, statt es zu wiederholen.
            </span>
          </div>
          <div className="knopfreihe">
            <button
              type="button"
              className="knopf"
              onClick={() => {
                setzeNeuerVertrag(null);
              }}
            >
              Abbrechen
            </button>
            <button
              type="button"
              className="knopf knopf--haupt"
              disabled={laedt || neuerVertrag.kennung.trim() === ''}
              onClick={() =>
                void handgriff(async () => {
                  await vereinbarungAnlegen(ressource, {
                    kennung: neuerVertrag.kennung.trim(),
                    gueltigAb: neuerVertrag.gueltigAb,
                    gueltigBis: neuerVertrag.gueltigBis === '' ? null : neuerVertrag.gueltigBis,
                    kuendigungsfristTage:
                      neuerVertrag.kuendigungsfristTage.trim() === ''
                        ? null
                        : Number(neuerVertrag.kuendigungsfristTage),
                    bedingungen:
                      neuerVertrag.bedingungen.trim() === ''
                        ? null
                        : neuerVertrag.bedingungen.trim(),
                    /*
                     * ALS ENTWURF. Eine Vereinbarung wird aktiv, wenn sie
                     * unterschrieben ist — die Datenbank verlangt dafür ein
                     * Datum (0185). „Aktiv" beim Anlegen anzubieten hieße, die
                     * Unterschrift zu behaupten.
                     */
                    status: 'entwurf',
                    unterzeichnetAm: null,
                    bemerkung: null,
                  });
                  setzeNeuerVertrag(null);
                }, 'Die Vereinbarung ist als Entwurf angelegt. Sie gilt, sobald sie unterschrieben ist.')
              }
            >
              Anlegen
            </button>
          </div>
        </>
      )}

      {/* ==================================================================
          DIE ABSAGESTAFFEL (RES-86)
          ==================================================================

          Sie hängt an der GÜLTIGEN Vereinbarung. Ohne eine gibt es nichts, woran
          sie hängen könnte — und eine Staffel an einem Entwurf wäre eine
          Vereinbarung, die niemand unterschrieben hat. */}
      {gueltig !== null && (
        <>
          <h3 className="karte__unterteil">Absagefristen und Ausfallvergütung</h3>
          <p className="feld__hilfe">
            Lesart: <strong>weniger als</strong> so viele Tage vor dem Einsatz kostet die
            Absage den genannten Wert. Treffen mehrere Stufen, gilt die strengste. Die letzte
            kostenfreie Absage ergibt sich aus der höchsten Stufe.
          </p>

          {gueltig.staffel.length === 0 ? (
            <p className="liste__leer">
              Keine Staffel hinterlegt — eine Absage kostet damit bis zum Beginn nichts.
            </p>
          ) : (
            <div className="tabellenhuelle tabellenhuelle--telefon">
              <table className="liste__tabelle" aria-label="Absagefristen">
                <thead>
                  <tr>
                    <th scope="col">Weniger als</th>
                    <th scope="col">Berechnung</th>
                    <th scope="col">Wert</th>
                    {darfHonorarAendern && <th scope="col">Handlung</th>}
                  </tr>
                </thead>
                <tbody>
                  {gueltig.staffel.map((s) => (
                    <tr key={s.id}>
                      <th scope="row">{s.abTagenVorEinsatz} Tage vorher</th>
                      <td>{s.berechnung === 'prozent' ? 'Prozent vom Honorar' : 'Pauschale'}</td>
                      <td className="zahl">
                        {s.wert === null ? (
                          /* SYS-03: die Frist sieht jeder, den Betrag nicht. */
                          <span className="hilfe">nicht einsehbar</span>
                        ) : (
                          s.berechnung === 'prozent' ? alsProzent(s.wert) : alsEurosatz(s.wert)
                        )}
                      </td>
                      {darfHonorarAendern && (
                        <td>
                          <button
                            type="button"
                            className="knopf knopf--eingriff knopf--klein"
                            disabled={laedt}
                            onClick={() => {
                              setzeNachfrage({ art: 'stufe', vertrag: gueltig, stufe: s });
                            }}
                          >
                            Entfernen …
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {darfHonorarAendern
            && (neueStufe?.vereinbarung === gueltig.id ? (
              <>
                <Feldraster>
                  <div className="feld">
                    <label htmlFor="st-tage">Weniger als … Tage vorher</label>
                    <input
                      id="st-tage"
                      className="feld__eingabe feld__eingabe--schmal"
                      inputMode="numeric"
                      value={neueStufe.abTagenVorEinsatz}
                      onChange={(e) => {
                        setzeNeueStufe({ ...neueStufe, abTagenVorEinsatz: e.target.value });
                      }}
                    />
                  </div>
                  <div className="feld">
                    <label htmlFor="st-art">Berechnung</label>
                    <select
                      id="st-art"
                      value={neueStufe.berechnung}
                      onChange={(e) => {
                        setzeNeueStufe({
                          ...neueStufe,
                          berechnung: e.target.value as 'prozent' | 'pauschale',
                        });
                      }}
                    >
                      <option value="prozent">Prozent vom Honorar</option>
                      <option value="pauschale">Pauschale</option>
                    </select>
                  </div>
                  <div className="feld">
                    <label htmlFor="st-wert">
                      {neueStufe.berechnung === 'prozent' ? 'Prozent' : 'Betrag (netto)'}
                    </label>
                    <input
                      id="st-wert"
                      className="feld__eingabe feld__eingabe--schmal"
                      inputMode="decimal"
                      value={neueStufe.wert}
                      onChange={(e) => {
                        setzeNeueStufe({ ...neueStufe, wert: e.target.value });
                      }}
                    />
                  </div>
                </Feldraster>
                <div className="knopfreihe">
                  <button
                    type="button"
                    className="knopf"
                    onClick={() => {
                      setzeNeueStufe(null);
                    }}
                  >
                    Abbrechen
                  </button>
                  <button
                    type="button"
                    className="knopf knopf--haupt"
                    disabled={
                      laedt
                      || neueStufe.wert.trim() === ''
                      || neueStufe.abTagenVorEinsatz.trim() === ''
                    }
                    onClick={() =>
                      void handgriff(async () => {
                        await staffelSetzen(ressource, gueltig.id, {
                          abTagenVorEinsatz: Number(neueStufe.abTagenVorEinsatz),
                          berechnung: neueStufe.berechnung,
                          wert: neueStufe.wert.trim(),
                          bemerkung: null,
                        });
                        setzeNeueStufe(null);
                      }, 'Die Stufe ist gesetzt.')
                    }
                  >
                    Stufe setzen
                  </button>
                </div>
              </>
            ) : (
              <button
                type="button"
                className="knopf knopf--klein"
                onClick={() => {
                  setzeNeueStufe({
                    vereinbarung: gueltig.id,
                    abTagenVorEinsatz: '',
                    berechnung: 'prozent',
                    wert: '',
                  });
                }}
              >
                Stufe hinzufügen
              </button>
            ))}
        </>
      )}

      {/* ==================================================================
          DER EIGENE HONORARSATZ (RES-69)
          ==================================================================

          „Eine dienstleisterspezifische Tabelle hat Vorrang." Was hier steht,
          schlägt die allgemeine Vorgabe — und `wirksam` zeigt, was daraus
          folgt: der Betrag, der für diesen Trainer heute gilt, samt Stufe. */}
      {darfHonorar && honorar !== null && (
        <>
          <h3 className="karte__unterteil">Honorar</h3>
          <p className="feld__hilfe">
            Netto. Das Gitter zeigt, was <strong>für diesen Trainer heute gilt</strong>: sein
            eigener Satz, wo er einen hat, sonst die allgemeine Vorgabe des Hauses — sie ist
            als „Vorgabe" gekennzeichnet. Wer hier einen Betrag einträgt, legt einen
            eigenen Satz an, und der hat ab dann Vorrang.
          </p>

          {/* ----------------------------------------------------------------
              OHNE VEREINBARUNG KEIN EIGENER SATZ (0191)

              Seit die Stufe am GEFÄSS hängt, braucht ein eigener Satz zuerst
              eine eigene Honorartabelle. Das ist kein Umweg, sondern die Sache
              selbst: „plus die Möglichkeit der individuellen Vereinbarungen mit
              den Trainern selbst" (Auftraggeber am 12.09.2026) — eine
              Vereinbarung hat einen Beginn, und den muss jemand nennen.

              Vorher entstand ein eigener Satz beiläufig beim ersten Tippen im
              Gitter, mit „gültig ab heute" und ohne dass irgendwo stand, seit
              wann etwas anderes vereinbart ist. */}
          {eigene === undefined ? (
            <div className="hinweiskarte">
              <p>
                Für diesen Trainer gilt die <strong>allgemeine Vorgabe</strong> des Hauses.
                Eine eigene Vereinbarung gibt es nicht.
              </p>
              {darfHonorarAendern && (
                <>
                  <Feldraster>
                    <div className="feld">
                      <label htmlFor="tv-eigenab">gültig ab</label>
                      <input
                        id="tv-eigenab"
                        type="date"
                        className="feld__eingabe feld__eingabe--schmal"
                        value={eigeneAb}
                        onChange={(e) => {
                          setzeEigeneAb(e.target.value);
                        }}
                      />
                      <p className="feld__hilfe">
                        Der Tag, ab dem das Vereinbarte gilt. Ab dann schlägt seine Tabelle
                        die allgemeine Vorgabe.
                      </p>
                    </div>
                  </Feldraster>
                  {/* ------------------------------------------------------------
                      DIE HAUSSÄTZE ÜBERNEHMEN (0192)

                      Auftraggeber am 12.09.2026: „honorartabellen sollen
                      kopierbar sein." Hier ist der Fall, der das nötig macht: Er
                      soll die Sätze des Hauses bekommen und in zwei Zellen etwas
                      anderes. Ohne dieses Häkchen wären das sechsundsiebzig
                      getippte Beträge, von denen vierundsiebzig schon woanders
                      stehen.

                      VORBELEGT AUS. Eine leere Vereinbarung ist der ehrlichere
                      Ausgangspunkt: Was darin nicht steht, kommt ohnehin aus der
                      allgemeinen Tabelle (0192). Wer kopiert, entscheidet sich
                      dafür, die Beträge fortan getrennt zu pflegen. */}
                  {allgemeine !== undefined && (
                    <div className="feld">
                      <label htmlFor="tv-uebernehmen">
                        <input
                          id="tv-uebernehmen"
                          type="checkbox"
                          checked={uebernehmen}
                          onChange={(e) => {
                            setzeUebernehmen(e.target.checked);
                          }}
                        />{' '}
                        Die Beträge aus <code>{allgemeine.kennung}</code> übernehmen
                      </label>
                      <p className="feld__hilfe">
                        Ohne Häkchen bleibt die Vereinbarung leer — und jede Zelle nimmt
                        weiter den Eintrag aus der allgemeinen Tabelle. Mit Häkchen stehen
                        die Beträge <strong>eigenständig</strong> in seiner Tabelle: Eine
                        spätere Änderung am Haussatz wirkt dann nicht mehr auf ihn.
                      </p>
                    </div>
                  )}
                  <div className="knopfreihe">
                    <button
                      type="button"
                      className="knopf knopf--klein"
                      disabled={laedt || eigeneAb === ''}
                      onClick={() => {
                        const felder = {
                          /*
                            Die Kennung kommt aus der Kennung des Trainers —
                            lesbar und stabil. Sie steht später in jeder
                            Auskunft darüber, woher ein Satz kam.
                          */
                          kennung: `VEREINBARUNG-${honorar.kennung.toUpperCase()}`,
                          bezeichnung: `Eigene Vereinbarung — ${honorar.bezeichnung}`,
                          ressource,
                          gueltigAb: eigeneAb,
                          gueltigBis: null,
                          bemerkung: null,
                        };
                        anpacken(
                          () =>
                            uebernehmen
                              ? /*
                                  MIT DEN HAUSSÄTZEN (0192). Das ist der Fall, um
                                  den es fast immer geht: Er bekommt die Sätze des
                                  Hauses und in zwei Zellen etwas anderes. Kopiert
                                  wird der Stand, der am Beginn gilt.
                                */
                                tabelleKopieren({
                                  ...felder,
                                  vorlage: allgemeine!.id,
                                  stichtag: null,
                                })
                              : tabelleAnlegen(felder),
                          uebernehmen
                            ? `Die eigene Vereinbarung gilt ab ${eigeneAb}, mit den `
                              + 'Beträgen des Hauses. Eine geleerte Zelle nimmt sie wieder '
                              + 'aus der allgemeinen Tabelle.'
                            : `Die eigene Vereinbarung gilt ab ${eigeneAb}. Die Beträge fehlen noch.`,
                        );
                      }}
                    >
                      Eigene Vereinbarung anlegen
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <p className="feld__hilfe">
              Seine Vereinbarung <code>{eigene.kennung}</code> gilt ab {eigene.gueltigAb}
              {eigene.gueltigBis === null ? ' und ist unbefristet' : ` bis ${eigene.gueltigBis}`}.
              Was darin steht, schlägt die allgemeine Vorgabe — was fehlt, kommt von dort.
            </p>
          )}

          <Honorargitter
            wirksam={honorar.wirksam}
            stichtag={stichtag}
            beiStichtag={setzeStichtag}
            /*
              OHNE EIGENE VEREINBARUNG IST DAS GITTER EINE ANSICHT. `tabelle` ist
              dann leer, `darfAendern` false — man sieht, was gilt, und kann
              nichts eintragen, weil es keinen Ort dafür gibt.
            */
            tabelle={eigene?.id ?? ''}
            stufe="ressource"
            darfAendern={darfHonorarAendern && eigene !== undefined}
            laedt={laedt}
            beiHandgriff={anpacken}
          />

          {/* ----------------------------------------------------------------
              SEINE EIGENEN FASSUNGEN (RES-72)

              Nur seine — die allgemeinen stehen in der Honorartabelle unter
              Stammdaten. Ein Trainer, der seit drei Jahren dabei ist, hat hier
              die Geschichte seiner Sätze, und genau die verlangt RES-72. */}
          {honorar.saetze.length > 0 && (
            <>
              <div className="knopfreihe">
                <button
                  type="button"
                  className="knopf knopf--verweis"
                  aria-expanded={fassungen}
                  onClick={() => {
                    setzeFassungen(!fassungen);
                  }}
                >
                  {fassungen
                    ? 'Eigene Fassungen ausblenden'
                    : `Eigene Fassungen zeigen (${String(honorar.saetze.length)})`}
                </button>
              </div>

              {fassungen && (
                <div className="tabellenhuelle tabellenhuelle--telefon">
                  <table className="liste__tabelle" aria-label="Eigene Honorarsätze dieses Trainers">
                    <thead>
                      <tr>
                        <th scope="col">Kategorie</th>
                        <th scope="col">Tarif</th>
                        <th scope="col">Betrag</th>
                        <th scope="col">gültig ab</th>
                        <th scope="col">gültig bis</th>
                        {darfHonorarAendern && <th scope="col">Handlung</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {honorar.saetze.map((s) => (
                        <tr
                          key={s.id}
                          className={s.gueltigBis === null ? undefined : 'liste__zeile--blass'}
                        >
                          <th scope="row">{s.leistungsartName}</th>
                          <td>{TARIFNAME[s.tarif]}</td>
                          <td className="zahl">{alsEurosatz(s.betrag)}</td>
                          <td className="liste__einzeilig">{alsTag(s.gueltigAb)}</td>
                          <td className="liste__einzeilig">{alsTag(s.gueltigBis) ?? <span className="hilfe">offen</span>}</td>
                          {darfHonorarAendern && (
                            <td>
                              {/*
                               * RES-72: GEÄNDERT WIRD NICHT, GESCHLOSSEN WIRD.
                               * Danach gilt für diese Zelle wieder die allgemeine
                               * Vorgabe — und was beauftragt ist, behält seinen Satz.
                               * Deshalb KEINE RÜCKFRAGE (CRM-104): Der Satz bleibt
                               * als Fassung stehen, nichts geht verloren.
                               */}
                              {s.gueltigBis === null && (
                                <button
                                  type="button"
                                  className="knopf knopf--klein"
                                  disabled={laedt}
                                  onClick={() => {
                                    anpacken(
                                      () =>
                                        satzSchliessen(s.id, { gueltigBis: tagDavor(heute()) }),
                                      `Der eigene Satz für ${s.leistungsartName} `
                                        + `(${TARIFNAME[s.tarif]}) gilt bis ${tagDavor(heute())}.`,
                                    );
                                  }}
                                >
                                  Zum heutigen Tag schließen
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </>
      )}

      {nachfrage !== null && nachfrageFenster(nachfrage)}
    </Abschnitt>
  );

  /**
   * DIE RÜCKFRAGEN (CRM-104). Jede nennt, was danach gilt — und dass die
   * Maske keinen Weg zurück hat.
   */
  function nachfrageFenster(n: Nachfrage): JSX.Element {
    const schliessen = (): void => {
      setzeNachfrage(null);
    };
    switch (n.art) {
      case 'unterschreiben':
        return (
          <Rueckfrage
            frage="Vereinbarung als unterschrieben setzen?"
            unterzeile={`${n.vertrag.kennung} · unterschrieben am ${alsTag(heute()) ?? heute()}`}
            tat="Als unterschrieben setzen"
            beiBestaetigen={() =>
              bestaetigt(
                () =>
                  vereinbarungAendern(ressource, n.vertrag.id, {
                    status: 'aktiv',
                    unterzeichnetAm: heute(),
                  }),
                `${n.vertrag.kennung} ist ab heute unterschrieben und gilt.`,
              )
            }
            beiSchliessen={schliessen}
          >
            {/* RES-64, RES-65 */}
            <p>
              Die Vereinbarung gilt ab heute und wird verbindlich: Beauftragung und
              Absagestaffel stützen sich auf sie. Zurück auf Entwurf geht es danach nicht.
            </p>
          </Rueckfrage>
        );
      case 'kuendigen':
        return (
          <Rueckfrage
            frage="Vereinbarung kündigen?"
            unterzeile={`${n.vertrag.kennung} · gekündigt am ${alsTag(heute()) ?? heute()}`}
            tat="Kündigen"
            beiBestaetigen={() =>
              bestaetigt(
                () =>
                  vereinbarungAendern(ressource, n.vertrag.id, {
                    status: 'gekuendigt',
                    gekuendigtAm: heute(),
                  }),
                `${n.vertrag.kennung} ist gekündigt. Sie läuft bis zum Ende ihrer Laufzeit weiter.`,
              )
            }
            beiSchliessen={schliessen}
          >
            {/* RES-65: Kündigungsfrist und Laufzeit stehen an der Vereinbarung. RES-64: danach keine Beauftragung. */}
            <p>
              Sie läuft bis zum Ende ihrer Laufzeit weiter
              {n.vertrag.kuendigungsfristTage === null
                ? ''
                : ` (Kündigungsfrist ${String(n.vertrag.kuendigungsfristTage)} Tage)`}
              . Danach lässt sich der Trainer nur mit einer neuen Vereinbarung beauftragen.
              Die Kündigung lässt sich hier nicht zurücknehmen.
            </p>
          </Rueckfrage>
        );
      case 'stufe':
        return (
          <Rueckfrage
            frage="Stufe entfernen?"
            unterzeile={`${n.vertrag.kennung} · weniger als ${String(n.stufe.abTagenVorEinsatz)} Tage vorher`}
            tat="Entfernen"
            beiBestaetigen={() =>
              bestaetigt(
                () => staffelEntfernen(ressource, n.vertrag.id, n.stufe.id),
                'Die Stufe ist entfernt.',
              )
            }
            beiSchliessen={schliessen}
          >
            {/* RES-86: Treffen mehrere Stufen, gilt die strengste. */}
            <p>
              Eine Absage in dieser Frist kostet danach
              {n.stufe.wert === null
                ? ' nicht mehr den Wert dieser Stufe'
                : ` nicht mehr ${n.stufe.berechnung === 'prozent' ? alsProzent(n.stufe.wert) : alsEurosatz(n.stufe.wert)}`}
              , sondern was die übrigen Stufen sagen — ohne weitere Stufe nichts. Zurück
              kommt sie nur, wenn man sie neu setzt.
            </p>
          </Rueckfrage>
        );
    }
  }
}
